import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import {
  inheritedElevation,
  inheritedNeutral as neutral,
  inheritedRadius,
  inheritedSemantic,
  inheritedShadowInk,
  inheritedShadows,
  inheritedStatus,
  inheritedTypography,
  spacingUnit,
  typographyTokens,
} from './tokens';
import {
  SUITE_ACCENT_VARIABLE,
  suiteColors,
  suiteControls,
  suiteHeader,
  suiteLayout,
  suiteNav,
  suitePage,
  suiteTypography,
} from './tokens/suite';
import { densityComponentDefaults, mergeComponents } from './theme';
import type { ChromeTokens } from './variant';

import './augmentation';

/**
 * The `suite` chrome variant — the legacy EMIL Insurance Suite look as an MUI theme (SDD-012,
 * U-2/U-3/U-4). Light only: the legacy has no dark mode, so this theme declares one colour
 * scheme and the shell hides the toggle.
 *
 * What comes from the frozen tokens: the navy sidebar and its text, the white 70 px header and
 * its shadow, the page grey, Open Sans, the 4 px radius, the `#04a9f5` primary and the tenant
 * accent behind `--main-color`. What the tokens file is silent about — success/warning/error/info,
 * the status chips, the grey ramp — keeps the console's own AA-checked values: parity is owed
 * where the legacy has an opinion, and nowhere else. Since T-31 those console-own values are the
 * pre-T-31 Berry values, frozen in `tokens/suite-inherited.ts`, so the berry restyle cannot reach
 * suite (prompt 24 decision 2).
 *
 * Three pairs inherited from the legacy fail WCAG AA and are deliberately NOT corrected here
 * (recorded in SDD-012 §6 with their ratios): nav text on navy, white on the primary blue, and
 * `#888` secondary text on white. The redesign fixes them; the transition wears them.
 */

/** The accent, applied as the legacy applies it: a CSS variable the tenant's branding overrides. */
const accent = `var(${SUITE_ACCENT_VARIABLE}, ${suiteColors.accentDefault})`;

export const suiteChrome: ChromeTokens = {
  variant: 'suite',
  navWidth: suiteLayout.navWidth,
  navWidthCollapsed: suiteLayout.navWidthCollapsed,
  headerHeight: suiteLayout.headerHeight,
  lightOnly: true,
  // What prompt 22 (`f1552af`) gave the patterns, kept.
  surface: {
    cardRadius: inheritedRadius.lg,
    cardShadow: inheritedElevation.card,
    cardBorder: true,
    controlRadius: inheritedRadius.md,
    badgeRadius: inheritedRadius.lg,
    sectionAccentRule: true,
    headerBand: true,
    headerCard: false,
    filterBarTint: true,
    filterStyle: 'chips',
    fieldValue: { fontSize: '0.8125rem', fontWeight: 400 },
    grid: {
      frame: true,
      headerBackground: 'var(--mui-palette-surfaces-sunken)',
      headerColor: 'inherit',
      headerFontSize: '0.8125rem',
      cellFontSize: '0.8125rem',
      rowBorder: 'var(--mui-palette-divider)',
    },
  },
};

const suiteComponents: ThemeOptions['components'] = {
  MuiCssBaseline: {
    styleOverrides: {
      ':root': {
        '--eis-shadow-ink': inheritedShadowInk,
        [SUITE_ACCENT_VARIABLE]: suiteColors.accentDefault,
      },
      body: { overflowWrap: 'anywhere' },
      ':focus-visible': { outlineOffset: 2 },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: suiteControls.buttonFontWeight,
        borderRadius: suiteControls.buttonRadius,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: suiteControls.buttonRadius,
        color: 'var(--mui-palette-text-primary)',
        '&.Mui-selected': {
          color: 'var(--mui-palette-surfaces-selectedText)',
          backgroundColor: 'var(--mui-palette-surfaces-selected)',
          '&:hover': { backgroundColor: 'var(--mui-palette-surfaces-selected)' },
        },
      },
    },
  },
  MuiIconButton: { styleOverrides: { root: { borderRadius: suiteControls.buttonRadius } } },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: suiteControls.inputRadius },
      notchedOutline: { borderColor: suiteColors.inputBorder },
      input: { color: suiteColors.inputText },
    },
  },
  MuiChip: { styleOverrides: { root: { borderRadius: suiteControls.buttonRadius } } },
  /** The legacy card: a hairline border, no shadow, 4 px corners, 24 px padding. */
  MuiCard: {
    defaultProps: { elevation: 0, variant: 'outlined' },
    styleOverrides: {
      root: { borderRadius: suitePage.cardRadius, borderColor: suitePage.cardBorder },
    },
  },
  MuiCardHeader: {
    defaultProps: { slotProps: { title: { variant: 'h4', component: 'span' } } },
    styleOverrides: { root: { padding: `${String(suitePage.cardPadding)}px` } },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: suitePage.cardPadding,
        '&:last-child': { paddingBottom: suitePage.cardPadding },
      },
    },
  },
  MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
  MuiDialog: { styleOverrides: { paper: { borderRadius: suitePage.cardRadius } } },
  MuiMenu: { styleOverrides: { paper: { borderRadius: suitePage.cardRadius } } },
  MuiPopover: { styleOverrides: { paper: { borderRadius: suitePage.cardRadius } } },
  MuiAlert: {
    defaultProps: { variant: 'outlined' },
    styleOverrides: { root: { borderRadius: suitePage.cardRadius } },
  },
  /** The legacy nav item is a full-width row with a left-edge bar, not a pill. */
  MuiListItemButton: { styleOverrides: { root: { borderRadius: suiteNav.itemRadius } } },
  /** The legacy header: white, 70 px, floating on its own wide shadow. */
  MuiAppBar: {
    defaultProps: { elevation: 0, color: 'inherit' },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: suiteHeader.background,
        color: suiteHeader.iconColor,
        boxShadow: suiteHeader.shadow,
      },
    },
  },
  /** The legacy sidebar: navy, casting its shadow onto the page. */
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundImage: 'none',
        borderRight: 'none',
        backgroundColor: suiteNav.background,
        color: suiteNav.text,
        boxShadow: suiteNav.shadow,
      },
    },
  },
  MuiDivider: { styleOverrides: { root: { borderColor: 'var(--mui-palette-divider)' } } },
  MuiDataGrid: {
    // The pre-T-31 row height; berry's grew to the design's 44px.
    defaultProps: { rowHeight: 40 },
    styleOverrides: { root: { borderRadius: suitePage.cardRadius } },
  },
};

export const suiteThemeOptions: ThemeOptions = {
  eis: suiteChrome,
  cssVariables: { colorSchemeSelector: 'data-mui-color-scheme', cssVarPrefix: 'mui' },
  spacing: spacingUnit,
  shape: { borderRadius: suitePage.cardRadius },
  shadows: inheritedShadows as ThemeOptions['shadows'],
  typography: {
    // The pre-T-31 Berry type SCALE (sizes per variant) is kept: the legacy sets only a base size
    // and heading weight, and a console with two type scales would be two consoles.
    ...inheritedTypography,
    fontFamily: suiteTypography.fontFamily,
    htmlFontSize: 16,
    fontSize: suiteTypography.baseSize,
    fontWeightLight: suiteTypography.weights[0],
    fontWeightRegular: suiteTypography.weights[1],
    fontWeightMedium: suiteTypography.weights[2],
    fontWeightBold: suiteTypography.weights[2],
    h1: {
      fontSize: '1.75rem',
      fontWeight: suiteTypography.headingWeight,
      lineHeight: suiteTypography.headingLineHeight,
    },
    h2: {
      fontSize: '1.375rem',
      fontWeight: suiteTypography.headingWeight,
      lineHeight: suiteTypography.headingLineHeight,
    },
    h3: {
      fontSize: '1.125rem',
      fontWeight: suiteTypography.headingWeight,
      lineHeight: suiteTypography.headingLineHeight,
    },
    h4: {
      fontSize: '1rem',
      fontWeight: suiteTypography.headingWeight,
      lineHeight: suiteTypography.headingLineHeight,
    },
    h5: {
      fontSize: '0.9375rem',
      fontWeight: suiteTypography.headingWeight,
      lineHeight: suiteTypography.headingLineHeight,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: suiteTypography.headingWeight,
      lineHeight: suiteTypography.headingLineHeight,
    },
    body1: { fontSize: '0.875rem', lineHeight: suiteTypography.lineHeight },
    body2: { fontSize: '0.8125rem', lineHeight: suiteTypography.lineHeight },
    button: {
      fontSize: '0.8125rem',
      fontWeight: suiteControls.buttonFontWeight,
      textTransform: 'none',
    },
  },
  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: {
          main: suiteColors.primary,
          contrastText: neutral[0],
        },
        secondary: {
          main: suiteColors.accentDefault,
          contrastText: suiteColors.ink,
        },
        success: { main: inheritedSemantic.success, contrastText: neutral[0] },
        warning: { main: inheritedSemantic.warning, contrastText: neutral[0] },
        error: { main: inheritedSemantic.error, contrastText: neutral[0] },
        info: { main: inheritedSemantic.info, contrastText: neutral[0] },
        background: { default: suitePage.background, paper: suitePage.paper },
        text: {
          primary: suiteTypography.headingColor,
          secondary: suiteTypography.baseColor,
          // `suiteColors.inputBorder` (#ced4da) is a border hairline, ~1.3:1 on white — nowhere
          // near legible as body text. `neutral[600]` is the Berry grey ramp's own muted-but-
          // readable step, clearing AA on every surface this theme paints text.disabled onto
          // (see __tests__/contrast.test.ts, "disabled text (Suite)").
          disabled: neutral[600],
        },
        divider: suitePage.rule,
        status: inheritedStatus,
        surfaces: {
          nav: suiteNav.background,
          navSub: suiteNav.background,
          hairline: suitePage.rule,
          toolbar: suiteHeader.background,
          sunken: suitePage.background,
          raised: suitePage.paper,
          // Selection surfaces are used by toggles as well as by the nav; the legacy tints a
          // selected control with the page grey and marks the nav with the accent, not a fill.
          selected: suitePage.background,
          selectedText: suiteColors.ink,
          navText: suiteNav.text,
          navActive: accent,
          navSection: suiteNav.sectionLabel,
        },
        grey: {
          50: neutral[50],
          100: neutral[100],
          200: neutral[200],
          300: neutral[300],
          400: neutral[400],
          500: neutral[500],
          600: neutral[600],
          700: neutral[700],
          800: neutral[800],
          900: neutral[900],
        },
      },
    },
  },
  components: mergeComponents(densityComponentDefaults, suiteComponents),
};

export const suiteTheme: Theme = createTheme(suiteThemeOptions);

/** The mono token is shared: the suite has no opinion about code, and consoles show ERNs. */
export const suiteMonoFamily = typographyTokens.monoFamily;
